import { formatDateTime, formatMoney, ListView, PageHeader, useCursorList } from "@majitap/ui";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { FilterToolbar } from "@majitap/ui/arc/filter-toolbar/filter-toolbar";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { api, errorOf, ok } from "../../api";
import { useCompanyFormat } from "../../company";
import { messageFor } from "../../error-copy";
import { filterSearch } from "../../list-search";
import {
  METHOD,
  PAYMENT_STATE,
  type PaymentMethod,
  type PaymentPurpose,
  type PaymentState,
  PURPOSE,
  purposeOf,
} from "../../payments";
import styles from "./payments.module.css";
import { TillView, tillQueueQuery } from "./till";

const STATES = Object.keys(PAYMENT_STATE) as PaymentState[];

export const paymentFilters = filterSearch({
  view: ["payments", "till"] as const,
  status: STATES,
  method: Object.keys(METHOD) as PaymentMethod[],
  purpose: Object.keys(PURPOSE) as PaymentPurpose[],
  till: ["unmatched", "matched", "duplicate", "ignored"] as const,
});
type PaymentSearch = ReturnType<typeof paymentFilters.validate>;

/** Payments (P8-15, `Console / Payments` in the design): every payment, and till money to match. */
export function PaymentsPage(): ReactNode {
  const search = useSearch({ from: "/console/payments" });
  const navigate = useNavigate({ from: "/payments" });
  const view = search.view ?? "payments";
  const queue = useQuery(tillQueueQuery);
  const waiting = queue.data?.length ?? 0;
  return (
    <>
      <PageHeader title="Payments" description="Money in and out, till payments to match, and refunds." />
      <SegmentedControl
        label="Show"
        value={view}
        onValueChange={(v) => navigate({ search: { view: v === "payments" ? undefined : "till" } })}
        options={[
          { value: "payments", label: "Payments" },
          { value: "till", label: waiting ? `Till to match · ${waiting}` : "Till to match" },
        ]}
      />
      {view === "payments" ? <PaymentList search={search} /> : <TillView state={search.till ?? "unmatched"} />}
    </>
  );
}

function PaymentList({ search }: { search: PaymentSearch }) {
  const navigate = useNavigate({ from: "/payments" });
  const format = useCompanyFormat();
  const { view: _v, till: _t, ...query } = search;
  const list = useCursorList({
    queryKey: ["payments", "list", query],
    fetchPage: (cursor) => ok(api.GET("/v1/admin/payments", { params: { query: { ...query, cursor, limit: 25 } } })),
  });
  const set = (patch: Partial<PaymentSearch>) => navigate({ search: (s) => ({ ...s, ...patch }) });
  const chips = [
    ...(search.method ? [{ id: "method", label: "Method", value: METHOD[search.method] }] : []),
    ...(search.purpose ? [{ id: "purpose", label: "For", value: PURPOSE[search.purpose] }] : []),
  ];
  return (
    <ListView
      caption="Payments"
      itemName={{ one: "payment", other: "payments" }}
      rows={list.rows}
      rowKey={(p) => p.id}
      status={list.status}
      errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
      onRetry={list.refetch}
      hasMore={list.hasMore}
      loadingMore={list.loadingMore}
      onLoadMore={list.loadMore}
      filtered={Boolean(search.status || search.method || search.purpose)}
      onResetFilters={() => navigate({ search: {} })}
      empty={{ title: "No payments yet", description: "M-Pesa, wallet, cash and refunds all show here." }}
      toolbar={
        <>
          <SegmentedControl
            label="Status"
            value={search.status ?? "all"}
            onValueChange={(v) => set({ status: v === "all" ? undefined : (v as PaymentState) })}
            options={[
              { value: "all", label: "All" },
              ...(["succeeded", "processing", "pending", "failed", "cancelled"] as const).map((s) => ({
                value: s,
                label: PAYMENT_STATE[s][0],
              })),
            ]}
          />
          <div className={styles.toolbarEnd}>
            <FilterToolbar
              filters={chips}
              onRemove={(id) => set({ [id]: undefined })}
              onClearAll={() => navigate({ search: {} })}
              addFilter={{
                fields: [
                  {
                    id: "method",
                    label: "Method",
                    options: (Object.keys(METHOD) as PaymentMethod[]).map((m) => ({ value: m, label: METHOD[m] })),
                  },
                  {
                    id: "purpose",
                    label: "For",
                    options: (Object.keys(PURPOSE) as PaymentPurpose[]).map((p) => ({ value: p, label: PURPOSE[p] })),
                  },
                ],
                onAdd: (chip, field) => set({ [field.id]: chip.value } as Partial<PaymentSearch>),
              }}
            />
          </div>
        </>
      }
      columns={[
        {
          key: "created_at",
          label: "When",
          render: (p) => <span className={styles.muted}>{formatDateTime(p.succeeded_at ?? p.created_at, format)}</span>,
        },
        {
          key: "reference",
          label: "Reference",
          render: (p) => (
            <Link to="/payments/$paymentId" params={{ paymentId: p.id }} className={styles.reference}>
              {p.reference}
            </Link>
          ),
        },
        {
          key: "receipt_number",
          label: "Receipt",
          render: (p) => <span className={styles.mono}>{p.receipt_number ?? "—"}</span>,
        },
        { key: "payer", label: "Payer", render: (p) => p.payer?.name ?? "—" },
        {
          key: "purpose",
          label: "For",
          render: (p) => <span className={styles.muted}>{purposeOf(p)}</span>,
        },
        { key: "method", label: "Method", render: (p) => METHOD[p.method] },
        {
          key: "amount",
          label: "Amount",
          numeric: true,
          render: (p) => (
            <span className={p.direction === "outbound" ? styles.out : undefined}>
              {p.direction === "outbound" ? "Out " : ""}
              {formatMoney(p.amount, { ...format, currency: p.currency })}
            </span>
          ),
        },
        {
          key: "status",
          label: "Status",
          render: (p) => <Badge tone={PAYMENT_STATE[p.status][1]}>{PAYMENT_STATE[p.status][0]}</Badge>,
        },
      ]}
    />
  );
}
